import { useEffect, useState, useCallback } from "react";
import Select from "../../components/Select";

const API = process.env.REACT_APP_API_URL || "";

const VIEW_TABS = [
  { key: "yearly", label: "연별" },
  { key: "monthly", label: "월별" },
  { key: "daily", label: "일별" },
];

const PAYMENT_OPTIONS = [
  { key: "", label: "전체 결제수단" },
  { key: "paypal", label: "PayPal" },
  { key: "tosspay", label: "토스페이" },
  { key: "kakaopay", label: "카카오페이" },
];

export default function AdminRevenue() {
  const [view, setView] = useState("monthly");
  const [year, setYear] = useState(new Date().getFullYear());
  const [yearFrom, setYearFrom] = useState(new Date().getFullYear());
  const [yearTo, setYearTo] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ view, year });
      if (view === "yearly") {
        params.set("year_from", yearFrom);
        params.set("year_to", yearTo);
      }
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (view === "monthly") params.set("month", month);
      if (view === "daily") params.set("month", month);
      if (paymentMethod) params.set("payment_method", paymentMethod);
      const res = await fetch(`${API}/api/revenue?${params}`);
      setData(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [view, year, yearFrom, yearTo, month, dateFrom, dateTo, paymentMethod]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function changeYear(dir) { setYear((y) => y + dir); }
  function changeMonth(dir) {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    setYear(d.getFullYear());
  }

  function downloadExcel() {
    if (!data?.rows) return;
    const headers = ["기간", "주문수", "출력비", "배송비", "결제합계", "환불합계", "순매출"];
    const csvRows = [headers.join(",")];
    for (const r of data.rows) {
      csvRows.push([r.label, r.orders, r.printCost, r.shipping, r.total, r.refund, r.net].join(","));
    }
    if (data.summary) {
      csvRows.push(["합계", data.summary.orders, data.summary.printCost, data.summary.shipping, data.summary.total, data.summary.refund, data.summary.net].join(","));
    }
    const bom = "\uFEFF";
    const blob = new Blob([bom + csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `매출내역_${view}_${year}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  const rows = data?.rows || [];
  const summary = data?.summary || {};
  const now = new Date();
  const isCurrentYear = year === now.getFullYear();
  const isCurrentMonth = month === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  return (
    <div className="admin-page">
      <div className="admin-title">매출 관리</div>

      {/* 요약 카드 */}
      <div className="admin-cards" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
        <div className="admin-card">
          <h3>결제 합계</h3>
          <div className="big">{(summary.total || 0).toLocaleString()}원</div>
        </div>
        <div className="admin-card">
          <h3>환불 합계</h3>
          <div className="big" style={{ color: "#ef4444" }}>{(summary.refund || 0).toLocaleString()}원</div>
        </div>
        <div className="admin-card">
          <h3>순매출</h3>
          <div className="big">{(summary.net || 0).toLocaleString()}원</div>
        </div>
        <div className="admin-card">
          <h3>주문 건수</h3>
          <div className="big">{summary.orders || 0}건</div>
        </div>
      </div>

      {/* 필터 */}
      <div className="admin-section">
        <div className="rv-filter-row">
          <div className="rv-tabs">
            {VIEW_TABS.map((t) => (
              <button key={t.key} className={`rv-tab ${view === t.key ? "active" : ""}`} onClick={() => setView(t.key)}>
                {t.label}
              </button>
            ))}
          </div>

          {/* 연별: 시작연도 ~ 종료연도 */}
          {view === "yearly" && (
            <div className="rv-date-select">
              <Select value={yearFrom} onChange={(e) => {
                const v = Number(e.target.value);
                setYearFrom(v);
                if (v > yearTo) setYearTo(v);
              }}>
                {Array.from({ length: 10 }, (_, i) => now.getFullYear() - i).map((y) => (
                  <option key={y} value={y}>{y}년</option>
                ))}
              </Select>
              <span className="ao-filter-sep">~</span>
              <Select value={yearTo} onChange={(e) => setYearTo(Number(e.target.value))}>
                {Array.from({ length: 10 }, (_, i) => now.getFullYear() - i).filter(y => y >= yearFrom).map((y) => (
                  <option key={y} value={y}>{y}년</option>
                ))}
              </Select>
            </div>
          )}

          {/* 월별: 시작월 ~ 종료월 */}
          {view === "monthly" && (
            <div className="rv-date-select">
              <input type="month" value={dateFrom || `${year}-01`} onChange={(e) => {
                const val = e.target.value;
                setDateFrom(val);
                setYear(Number(val.split("-")[0]));
                if (dateTo && val > dateTo) setDateTo(val);
              }} />
              <span className="ao-filter-sep">~</span>
              <input type="month" value={dateTo || `${year}-12`} min={dateFrom || ""} onChange={(e) => setDateTo(e.target.value)} />
              {(dateFrom || dateTo) && (
                <button className="rv-reset-btn" onClick={() => { setDateFrom(""); setDateTo(""); }}>초기화</button>
              )}
            </div>
          )}

          {/* 일별: 날짜 범위 */}
          {view === "daily" && (
            <div className="rv-date-select">
              <input type="date" value={dateFrom} onChange={(e) => {
                const val = e.target.value;
                setDateFrom(val);
                if (!dateTo || val > dateTo) setDateTo(val);
              }} />
              <span className="ao-filter-sep">~</span>
              <input type="date" value={dateTo} min={dateFrom || ""} onChange={(e) => setDateTo(e.target.value)} />
              {(dateFrom || dateTo) && (
                <button className="rv-reset-btn" onClick={() => { setDateFrom(""); setDateTo(""); }}>초기화</button>
              )}
            </div>
          )}

          <Select className="admin-select" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
            {PAYMENT_OPTIONS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </Select>

          <button className="rv-excel-btn" onClick={downloadExcel}>엑셀 다운로드</button>
        </div>

        {/* 테이블 */}
        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: "#888" }}>로딩 중...</div>
        ) : (
          <table className="admin-table rv-table">
            <thead>
              <tr>
                <th>기간</th>
                <th>주문수</th>
                <th>출력비</th>
                <th>배송비</th>
                <th>결제합계</th>
                <th>환불합계</th>
                <th>순매출</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: "center", color: "#999", padding: 20 }}>데이터가 없습니다.</td></tr>
              ) : rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.label}</td>
                  <td>{r.orders}</td>
                  <td>{r.printCost.toLocaleString()}</td>
                  <td>{r.shipping.toLocaleString()}</td>
                  <td className="rv-total">{r.total.toLocaleString()}</td>
                  <td className="rv-refund">{r.refund > 0 ? r.refund.toLocaleString() : "0"}</td>
                  <td className="rv-net">{r.net.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="rv-summary-row">
                <td>합계</td>
                <td>{summary.orders || 0}</td>
                <td>{(summary.printCost || 0).toLocaleString()}</td>
                <td>{(summary.shipping || 0).toLocaleString()}</td>
                <td className="rv-total">{(summary.total || 0).toLocaleString()}</td>
                <td className="rv-refund">{(summary.refund || 0).toLocaleString()}</td>
                <td className="rv-net">{(summary.net || 0).toLocaleString()}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}
